import type { FC, ReactNode } from 'react';
import { useContext } from 'react';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';

import Dota2Map from 'app/components/DesignVersion/Grad/Dota2Map';
import GoldAdvantageChart from 'app/components/GoldAdvantageChart';
import ThemeContext from 'app/common/ThemeContext';
import { useEmblaCarouselDotButtons } from 'app/hooks/useEmblaCarouselDotButtons';
import Dota2MapIcon from 'app/icons/dota2_map.svg';
import Dota2GoldIcon from 'app/icons/dota2_gold.svg';

import style from './style.module.scss';

// A carousel slide: minimap or gold chart, each gated by its own available
// field. The pagination is an icon toggle (map / gold) rather than plain dots,
// so every slide carries the icon + label of its pagination button.
interface Slide {
  key: 'map' | 'gold';
  Icon: SVG;
  label: string;
  className?: string;
  content: ReactNode;
}

interface Props {
  game: AggregatedGameDota2;
  team1: AggregatedTeamDota2;
  team2: AggregatedTeamDota2;
  opponent1: OpponentBase | undefined;
  opponent2: OpponentBase | undefined;
  // Slide gating, derived from the game's available_fields by the caller:
  // tower_status feeds the minimap, net_worth the gold curve.
  showMap: boolean;
  showGold: boolean;
}

const Dota2Carousel: FC<Props> = ({
  game,
  team1,
  team2,
  opponent1,
  opponent2,
  showMap,
  showGold
}) => {
  const { theme } = useContext(ThemeContext);
  const [emblaRef, emblaApi] = useEmblaCarousel();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useEmblaCarouselDotButtons(emblaApi);

  const radiantTeam = [team1, team2].find(team => team.side === 'radiant');
  const direTeam = [team1, team2].find(team => team.side === 'dire');

  const [radiantOpponent, direOpponent] =
    radiantTeam?.id === opponent1?.id ? [opponent1, opponent2] : [opponent2, opponent1];

  const winner = game.teams.find(team => team.id === game.winner_id)?.side ?? null;

  const slides: Slide[] = [
    ...(showMap
      ? [
          {
            key: 'map' as const,
            Icon: Dota2MapIcon,
            label: 'map view',
            content: (
              <Dota2Map
                dire={direTeam}
                radiant={radiantTeam}
                winner={winner}
                className={style.map}
              />
            )
          }
        ]
      : []),
    ...(showGold
      ? [
          {
            key: 'gold' as const,
            Icon: Dota2GoldIcon,
            label: 'gold graph view',
            className: style.chartSlide,
            content: (
              <GoldAdvantageChart
                className={style.goldChart}
                height="100%"
                goldAdvantage={game.radiant_gold_adv}
                goldAdvantageTeamInfo={radiantOpponent}
                secondTeamInfo={direOpponent}
                goldAvantageTeam="radiant"
                display="dota-2-scoreboard"
                advantageColor={theme.vg_dota_radiant}
                disadvantageColor={theme.vg_dota_dire}
              />
            )
          }
        ]
      : [])
  ];

  return (
    <div className={style.carousel}>
      <div ref={emblaRef} className={style.viewport}>
        <div className={style.container}>
          {slides.map(slide => (
            <div key={slide.key} className={classNames(style.slide, slide.className)}>
              {slide.content}
            </div>
          ))}
        </div>
      </div>
      {/* A lone slide has nothing to toggle to. */}
      {slides.length > 1 && (
        <div className={style.toggle}>
          {scrollSnaps.map((_, index) => {
            const { key, Icon, label } = slides[index] ?? slides[0];
            return (
              <button
                key={key}
                type="button"
                onClick={() => onDotButtonClick(index)}
                className={classNames(style.toggleButton, {
                  [style.toggleButtonSelected]: index === selectedIndex
                })}
                aria-label={label}
              >
                <Icon className={style.toggleIcon} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

Dota2Carousel.displayName = 'Dota2Carousel';

export default Dota2Carousel;
